import styled from "@emotion/styled";
import type { FC } from "react";
import { IconVideoRecorderSolid } from "src/constants/icons";
import { cmsImageUrl } from "src/lib/cmsImageUrl";
import Translate from "src/ui/components/Translate/Translate";

const PHOTO_OPTIONS = { quality: 75, autoFormat: true } as const;

/** Widths cover each card's crop at ~3x — the back and middle photos are zoomed past their card, the front one fills it 1:1. */
const backPhoto = cmsImageUrl(
  "26ed6981ce488ac50bfc8e7b6411e70117fe9ec2-2460x2528.png",
  { ...PHOTO_OPTIONS, width: 820 }
);
const middlePhoto = cmsImageUrl(
  "f018a9836c01f0160f46a8008bf6d98ea3b73ed4-2616x1600.png",
  { ...PHOTO_OPTIONS, width: 1000 }
);
const frontPhoto = cmsImageUrl(
  "a7f83fee7ae3ceead569751fd63b66c260569d25-2616x1600.png",
  { ...PHOTO_OPTIONS, width: 800 }
);

const Stage = styled.div`
  position: relative;
  width: 100%;
  max-width: 20.4375rem;
  margin: 0 auto;
  aspect-ratio: 327 / 172;
  filter: drop-shadow(-5px 5px 8px rgba(69, 49, 22, 0.08))
    drop-shadow(-20px 20px 14.5px rgba(69, 49, 22, 0.07))
    drop-shadow(-46px 45px 19.5px rgba(69, 49, 22, 0.03));
`;

const Card = styled.div`
  position: absolute;
  overflow: hidden;
  border-radius: 1rem;
  background: var(--color-white, #fff);

  img {
    position: absolute;
    display: block;
    max-width: none;
    object-fit: cover;
  }

  &:nth-of-type(1) {
    left: 51.52%;
    top: 4.4%;
    width: 31.12%;
    height: 88.4%;
    transform: rotate(9.33deg);

    img {
      left: -68.34%;
      top: -13.73%;
      width: 266.11%;
      height: 183.04%;
    }
  }

  &:nth-of-type(2) {
    left: 36.4%;
    top: 3.38%;
    width: 31.92%;
    height: 90.65%;
    transform: rotate(-0.78deg);

    img {
      left: -109.36%;
      top: -11.31%;
      width: 318.57%;
      height: 130.42%;
    }
  }

  &:nth-of-type(3) {
    left: 21.53%;
    top: 3.13%;
    width: 32.68%;
    height: 92.88%;
    transform: rotate(-7.81deg);

    img {
      inset: 0;
      width: 100%;
      height: 100%;
    }

    &::after {
      content: "";
      position: absolute;
      inset: 0;
      background: linear-gradient(
        to top,
        rgba(0, 0, 0, 0.65) 0%,
        rgba(0, 0, 0, 0) 29.84%
      );
    }
  }
`;

const CardLabel = styled.p`
  position: absolute;
  left: 8px;
  bottom: 10px;
  z-index: 1;
  margin: 0;
  font-family: var(--font-family);
  font-size: 9px;
  font-weight: 500;
  line-height: 8px;
  letter-spacing: -0.005em;
  color: #fff;
  opacity: 0.9;
  white-space: nowrap;
`;

const CardBadge = styled.div`
  position: absolute;
  right: 6px;
  bottom: 6px;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: rgba(33, 33, 33, 0.12);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  color: #fff;
`;

const CALL_CARDS = [backPhoto, middlePhoto, frontPhoto];

/** Card geometry is a percentage of the fixed 327x172 stage, so the fan scales as one. */
export const VideoCallCollage: FC = () => (
  <Stage aria-hidden="true">
    {CALL_CARDS.map((photo) => (
      <Card key={photo}>
        <img src={photo} alt="" />
        <CardLabel>
          <Translate msg="coachingBenefits.videoCallCard" />
        </CardLabel>
        <CardBadge>
          <IconVideoRecorderSolid />
        </CardBadge>
      </Card>
    ))}
  </Stage>
);
